import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "@/components/feature/LanguageSwitcher";

interface HeroSectionProps {
  onJoinClick: () => void;
  onWhitepaperClick: () => void;
}

function StarField() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let animId: number;
    let w = canvas.offsetWidth;
    let h = canvas.offsetHeight;
    canvas.width = w;
    canvas.height = h;

    const stars = Array.from({ length: 180 }, () => ({
      x: Math.random() * w,
      y: Math.random() * h,
      r: Math.random() * 1.4 + 0.2,
      speed: Math.random() * 0.3 + 0.05,
      opacity: Math.random(),
      dir: Math.random() > 0.5 ? 1 : -1,
      phase: Math.random() * Math.PI * 2,
    }));

    const draw = (t: number) => {
      ctx.clearRect(0, 0, w, h);
      stars.forEach((s) => {
        const flicker = 0.4 + 0.6 * Math.abs(Math.sin(t * 0.001 * s.speed * 3 + s.phase));
        ctx.beginPath();
        ctx.arc(s.x, s.y, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,220,150,${flicker * s.opacity})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };

    animId = requestAnimationFrame(draw);

    const onResize = () => {
      w = canvas.offsetWidth;
      h = canvas.offsetHeight;
      canvas.width = w;
      canvas.height = h;
    };
    window.addEventListener("resize", onResize);
    return () => {
      cancelAnimationFrame(animId);
      window.removeEventListener("resize", onResize);
    };
  }, []);

  return <canvas ref={canvasRef} className="absolute inset-0 w-full h-full pointer-events-none z-[1]" />;
}

function OrbitalRing({ size, mobileSize, duration, opacity, delay }: { size: number; mobileSize: number; duration: number; opacity: number; delay: number }) {
  return (
    <>
      {/* Desktop ring */}
      <div
        className="absolute rounded-full border border-amber-400/20 pointer-events-none hidden md:block"
        style={{
          width: size,
          height: size,
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          opacity,
          animation: `spin ${duration}s linear ${delay}s infinite`,
        }}
      >
        <div className="absolute w-2 h-2 rounded-full bg-amber-400/60" style={{ top: -4, left: "50%", transform: "translateX(-50%)" }} />
      </div>
      {/* Mobile ring */}
      <div
        className="absolute rounded-full border border-amber-400/20 pointer-events-none md:hidden"
        style={{
          width: mobileSize,
          height: mobileSize,
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          opacity,
          animation: `spin ${duration}s linear ${delay}s infinite`,
        }}
      >
        <div className="absolute w-1.5 h-1.5 rounded-full bg-amber-400/60" style={{ top: -3, left: "50%", transform: "translateX(-50%)" }} />
      </div>
    </>
  );
}

function ShootingStar({ delay, top, left }: { delay: number; top: string; left: string }) {
  return (
    <div
      className="absolute pointer-events-none"
      style={{ top, left, animation: `shootingStar 6s ease-in-out ${delay}s infinite`, opacity: 0 }}
    >
      <div className="w-24 h-px bg-gradient-to-r from-transparent via-amber-300/80 to-transparent" />
    </div>
  );
}

export default function HeroSection({ onJoinClick, onWhitepaperClick }: HeroSectionProps) {
  const { t } = useTranslation();
  const [scrollY, setScrollY] = useState(0);
  const [mounted, setMounted] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener("scroll", handleScroll, { passive: true });
    const timer = setTimeout(() => setMounted(true), 100);
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(timer);
    };
  }, []);

  return (
    <>
      <style>{`
        @keyframes spin {
          from { transform: translate(-50%, -50%) rotate(0deg); }
          to   { transform: translate(-50%, -50%) rotate(360deg); }
        }
        @keyframes shootingStar {
          0%   { opacity: 0; transform: translateX(0) translateY(0) rotate(-30deg); }
          5%   { opacity: 1; }
          30%  { opacity: 0; transform: translateX(200px) translateY(80px) rotate(-30deg); }
          100% { opacity: 0; transform: translateX(200px) translateY(80px) rotate(-30deg); }
        }
        @keyframes floatUp {
          0%   { opacity: 0; transform: translateY(40px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        @keyframes fadeIn {
          0%   { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes glowPulse {
          0%, 100% { box-shadow: 0 0 20px rgba(251,191,36,0.15); }
          50%       { box-shadow: 0 0 50px rgba(251,191,36,0.35), 0 0 100px rgba(251,191,36,0.1); }
        }
        @keyframes scanLine {
          0%   { transform: translateY(-100%); opacity: 0; }
          10%  { opacity: 0.4; }
          90%  { opacity: 0.4; }
          100% { transform: translateY(100vh); opacity: 0; }
        }
        .hero-badge   { animation: floatUp 0.8s ease forwards 0.2s; opacity: 0; }
        .hero-title   { animation: floatUp 0.9s ease forwards 0.5s; opacity: 0; }
        .hero-sub     { animation: floatUp 0.9s ease forwards 0.8s; opacity: 0; }
        .hero-btns    { animation: floatUp 0.9s ease forwards 1.0s; opacity: 0; }
        .hero-stats   { animation: floatUp 0.9s ease forwards 1.2s; opacity: 0; }
        .hero-nav     { animation: fadeIn 1s ease forwards 0.1s; opacity: 0; }
      `}</style>

      <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden">
        {/* Background */}
        <div
          className="absolute inset-0 bg-cover bg-center scale-110"
          style={{
            backgroundImage: `url('https://public.readdy.ai/ai/img_res/bff439dc078e8a5ee64e642abdac17c5.jpg')`,
            transform: `translateY(${scrollY * 0.25}px)`,
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black/85 z-[1]" />
        <div
          className="absolute left-0 right-0 h-px bg-gradient-to-r from-transparent via-amber-400/20 to-transparent z-[2] pointer-events-none"
          style={{ animation: "scanLine 8s linear 2s infinite" }}
        />
        <StarField />
        <div
          className="absolute inset-0 z-[2] opacity-[0.06]"
          style={{
            backgroundImage: "linear-gradient(rgba(255,200,50,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,200,50,1) 1px, transparent 1px)",
            backgroundSize: "80px 80px",
          }}
        />
        <div className="absolute inset-0 z-[2] pointer-events-none overflow-hidden">
          <OrbitalRing size={500} mobileSize={280} duration={30} opacity={0.15} delay={0} />
          <OrbitalRing size={750} mobileSize={420} duration={50} opacity={0.08} delay={-10} />
          <OrbitalRing size={1050} mobileSize={580} duration={80} opacity={0.05} delay={-20} />
        </div>
        <div className="absolute inset-0 z-[2] pointer-events-none overflow-hidden">
          <ShootingStar delay={2} top="20%" left="10%" />
          <ShootingStar delay={7} top="35%" left="60%" />
          <ShootingStar delay={13} top="15%" left="40%" />
          <ShootingStar delay={19} top="50%" left="20%" />
        </div>
        <div
          className="absolute z-[2] pointer-events-none rounded-full"
          style={{
            width: 600,
            height: 600,
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            background: "radial-gradient(circle, rgba(251,191,36,0.06) 0%, transparent 70%)",
            animation: "glowPulse 4s ease-in-out infinite",
          }}
        />

        {/* Navbar */}
        <nav className="hero-nav absolute top-0 left-0 right-0 z-20 flex items-center justify-between px-5 md:px-16 py-4 md:py-6">
          <div className="flex items-center gap-2 md:gap-3">
            <div className="w-7 h-7 md:w-8 md:h-8 flex items-center justify-center">
              <i className="ri-rocket-2-fill text-amber-400 text-xl md:text-2xl" style={{ filter: "drop-shadow(0 0 8px rgba(251,191,36,0.6))" }} />
            </div>
            <span className="text-white font-bold text-base md:text-xl tracking-[0.2em] md:tracking-[0.3em] uppercase" style={{ fontFamily: "'Orbitron', sans-serif" }}>
              ARTEMIDA
            </span>
          </div>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-8 text-sm text-white/70 tracking-widest uppercase">
            <a href="#timeline" className="hover:text-amber-400 transition-colors cursor-pointer">{t("nav_timeline")}</a>
            <a href="#invest" className="hover:text-amber-400 transition-colors cursor-pointer">{t("nav_invest")}</a>
            <a href="#technology" className="hover:text-amber-400 transition-colors cursor-pointer">{t("nav_technology")}</a>
            <a href="#roadmap" className="hover:text-amber-400 transition-colors cursor-pointer">{t("nav_roadmap")}</a>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <LanguageSwitcher />
            {/* Desktop join button */}
            <button
              onClick={onJoinClick}
              className="hidden md:block px-5 py-2 border border-amber-400/60 text-amber-400 text-xs tracking-widest uppercase hover:bg-amber-400/10 transition-all cursor-pointer whitespace-nowrap rounded-sm"
              style={{ fontFamily: "'Orbitron', sans-serif" }}
            >
              {t("nav_join")}
            </button>
            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden w-8 h-8 flex items-center justify-center text-white/70 hover:text-amber-400 transition-colors cursor-pointer"
            >
              <i className={`${mobileMenuOpen ? "ri-close-line" : "ri-menu-line"} text-xl`} />
            </button>
          </div>
        </nav>

        {/* Mobile menu dropdown */}
        {mobileMenuOpen && (
          <div className="absolute top-16 left-0 right-0 z-30 bg-black/90 backdrop-blur-md border-b border-white/10 px-5 py-4 flex flex-col gap-4 md:hidden">
            {[
              { href: "#timeline", label: t("nav_timeline") },
              { href: "#invest", label: t("nav_invest") },
              { href: "#technology", label: t("nav_technology") },
              { href: "#roadmap", label: t("nav_roadmap") },
            ].map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={() => setMobileMenuOpen(false)}
                className="text-white/70 text-sm tracking-widest uppercase hover:text-amber-400 transition-colors cursor-pointer py-1"
              >
                {item.label}
              </a>
            ))}
            <button
              onClick={() => { setMobileMenuOpen(false); onJoinClick(); }}
              className="mt-1 px-5 py-2.5 border border-amber-400/60 text-amber-400 text-xs tracking-widest uppercase hover:bg-amber-400/10 transition-all cursor-pointer whitespace-nowrap rounded-sm text-center"
              style={{ fontFamily: "'Orbitron', sans-serif" }}
            >
              {t("nav_join")}
            </button>
          </div>
        )}

        {/* Hero content */}
        <div className="relative z-10 text-center px-5 md:px-6 max-w-5xl mx-auto w-full pt-20 md:pt-0">
          {/* Badge */}
          <div className={`hero-badge inline-flex items-center gap-2 px-3 md:px-4 py-1.5 md:py-2 border border-amber-400/40 rounded-full mb-6 md:mb-8 bg-amber-400/5 backdrop-blur-sm ${mounted ? "" : ""}`}>
            <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
            <span className="text-amber-400 text-[10px] md:text-xs tracking-[0.15em] md:tracking-[0.2em] uppercase" style={{ fontFamily: "'Orbitron', sans-serif" }}>
              {t("hero_badge")}
            </span>
          </div>

          {/* Title */}
          <h1
            className="hero-title text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-black text-white leading-tight mb-5 md:mb-6"
            style={{ fontFamily: "'Orbitron', sans-serif", letterSpacing: "0.02em", textShadow: "0 0 60px rgba(251,191,36,0.15)" }}
          >
            {t("hero_title_1")}
            <br />
            <span
              className="text-transparent bg-clip-text bg-gradient-to-r from-amber-300 via-amber-400 to-orange-400"
              style={{ filter: "drop-shadow(0 0 30px rgba(251,191,36,0.4))" }}
            >
              {t("hero_title_2")}
            </span>
            <br />
            {t("hero_title_3")}
          </h1>

          {/* Subtitle */}
          <p className="hero-sub text-white/60 text-sm md:text-base lg:text-lg max-w-2xl mx-auto mb-8 md:mb-10 leading-relaxed px-2 md:px-0" style={{ fontFamily: "'Inter', sans-serif" }}>
            {t("hero_subtitle")}
          </p>

          {/* CTA buttons */}
          <div className="hero-btns flex flex-col sm:flex-row items-center justify-center gap-3 md:gap-4 px-4 sm:px-0">
            <button
              onClick={onJoinClick}
              className="relative w-full sm:w-auto px-8 py-3.5 md:py-4 bg-gradient-to-r from-amber-400 to-orange-500 text-black font-bold text-xs md:text-sm tracking-widest uppercase hover:from-amber-300 hover:to-orange-400 transition-all cursor-pointer whitespace-nowrap rounded-sm overflow-hidden group"
              style={{ fontFamily: "'Orbitron', sans-serif", boxShadow: "0 0 30px rgba(251,191,36,0.3)" }}
            >
              <span className="relative z-10">{t("hero_cta_join")}</span>
              <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full transition-transform duration-700 bg-gradient-to-r from-transparent via-white/20 to-transparent" />
            </button>
            <button
              onClick={onWhitepaperClick}
              className="w-full sm:w-auto px-8 py-3.5 md:py-4 border border-white/30 text-white text-xs md:text-sm tracking-widest uppercase hover:border-amber-400/60 hover:text-amber-400 transition-all cursor-pointer whitespace-nowrap rounded-sm backdrop-blur-sm"
              style={{ fontFamily: "'Orbitron', sans-serif" }}
            >
              <i className="ri-file-download-line mr-2" />
              {t("hero_cta_whitepaper")}
            </button>
          </div>

          {/* Stats */}
          <div className="hero-stats mt-10 md:mt-16 grid grid-cols-3 gap-4 md:gap-8 max-w-2xl mx-auto border-t border-white/10 pt-8 md:pt-10">
            {[
              { value: "2026", label: t("hero_stat_1_label") },
              { value: "4", label: t("hero_stat_2_label") },
              { value: "2030+", label: t("hero_stat_3_label") },
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div
                  className="text-xl md:text-2xl lg:text-3xl font-black text-amber-400 mb-1"
                  style={{ fontFamily: "'Orbitron', sans-serif", textShadow: "0 0 20px rgba(251,191,36,0.4)" }}
                >
                  {stat.value}
                </div>
                <div className="text-white/40 text-[9px] md:text-xs tracking-widest uppercase leading-tight">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-6 md:bottom-8 left-1/2 -translate-x-1/2 z-10 flex flex-col items-center gap-2 animate-bounce">
          <span className="text-white/30 text-[10px] md:text-xs tracking-widest uppercase">{t("hero_scroll")}</span>
          <i className="ri-arrow-down-line text-white/30 text-lg" />
        </div>
      </section>
    </>
  );
}
